import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ADMIN_IDLE_CHOICES, type AdminSessionSettings } from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Label } from '@london-life/ui/components/label';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { useRequest } from '../lib/use-request';

const label = (minutes: number) =>
  minutes % 60 === 0 ? `${minutes / 60} hour${minutes === 60 ? '' : 's'}` : `${minutes} minutes`;

// super admin only: how long an admin may leave the dashboard idle before it signs them out
export function AdminSessionsPage() {
  const current = useRequest(useCallback(() => api.admin.sessionSettings(), []));
  const [idle, setIdle] = useState<number>(30);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (current.data) setIdle(current.data.settings.idleMinutes);
  }, [current.data]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaved(false);
    try {
      await api.admin.saveSessionSettings({ idleMinutes: idle as AdminSessionSettings['idleMinutes'] });
      setSaved(true);
      await current.reload();
    } catch (problem) {
      current.fail(problem);
    }
  };
  const hours = current.data?.sessionHours ?? 12;
  return (
    <section>
      <PageHeader
        title="Admin sign-in"
        description="How long an admin can leave the dashboard without doing anything before it signs them out. Shorter is safer; a longer time suits building and testing."
      />
      {current.error && <ErrorNotice error={current.error} onRetry={() => void current.reload()} />}
      {current.data && (
        <form className="admin-panel admin-form" onSubmit={(event) => void submit(event)}>
          <div className="admin-field">
            <Label htmlFor="admin-idle">Sign out after being idle for</Label>
            <select
              id="admin-idle"
              className="content-select"
              value={idle}
              onChange={(event) => setIdle(Number(event.target.value))}
            >
              {ADMIN_IDLE_CHOICES.map((minutes) => (
                <option key={minutes} value={minutes} disabled={minutes > hours * 60}>
                  {label(minutes)}
                </option>
              ))}
            </select>
            <p className="admin-muted">
              Every admin, every device; it applies within a minute. An admin session also ends after {hours} hours
              however active, and saving needs a fresh two-step code.
            </p>
          </div>
          <div className="admin-page-actions">
            <Button type="submit">Save</Button>
            {saved && (
              <span role="status" className="admin-muted">
                Saved.
              </span>
            )}
          </div>
        </form>
      )}
    </section>
  );
}
